import { put, call, all, takeEvery, takeLatest } from "redux-saga/effects";
import { showModal, hideModal } from "../actions/modal.actions";
import { hideNotification } from "../actions/notifications.actions";
import { setToken } from "../actions/token.actions";
import {
  callAuthGetInternalAccounts,
  callSaveAuthPayoffCheck,
  callStoreDebitResponse,
  callStoreInternalResponse,
  callStorePaymentResponse,
  setAuthAdditionalDetails,
  setAuthError,
  setAuthLoans,
  setAuthRecordCreated,
  storeAuthCalcPostDate,
  storeAuthCalcPostDateV2,
  storeAuthInternalAccounts,
} from "../actions/authPayment.actions";
import {
  authCancelRecurring,
  authGetAdditionalLoanDetails,
  authGetInternalAccounts,
  authGetLoans,
  authSaveDebit,
  authSaveInternal,
  authSubmitAchPayment,
  authSubmitDebitPayment,
  authSubmitInternalTransfer,
  authSubmitInternalTransferToCreditCard,
  beginAuthSession,
  calculateAchPostDate,
  calculateAchPostDateV2,
  cancelFutureDatedPayment,
  checkPayoff,
  createAuthPaymentRecord,
  getAuthRefinanceRedirect,
  getScheduledPayments,
} from "../utils/requests";
import { getTokenRequest } from "../utils/requests";
import moment from "moment";

const showLoadingModal = () => {
  return showModal("loading", "Loading...");
};

const showErrorNotification = (message) => {
  return {
    type: "SHOW_NOTIFICATION",
    notificationType: "error",
    message: message,
  };
};

export function* beginAuthPaymentSession({ guid, history }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const token = yield call(getTokenRequest);
    sessionStorage.setItem("_LTjwt", token);
    yield put(setToken(token));

    yield put(showLoadingModal());
    yield call(beginAuthSession, guid);

    history.replace("/authenticated-payments");

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work

    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchBeginAuthPaymentSession() {
  yield takeEvery("BEGIN_AUTH_SESSION", beginAuthPaymentSession);
}

export function* authGetLoanList() {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const loans = yield call(authGetLoans);

    let scheduledPayments = [];
    let scheduledPaymentsResult = null;

    // Use a try catch here in case the scheduled payments request fails for some reason
    // This way we will catch it and still be able to display the loan list instead of redirecting to the auth error page
    try {
      scheduledPaymentsResult = yield call(getScheduledPayments);

      if (scheduledPaymentsResult) {
        scheduledPayments = scheduledPaymentsResult.ScheduledPayments;
      }
    } catch (error) {
      // Request didn't work
      console.log("Error getting scheduled payments", error);
    }

    // For each loan in loans, find the matching scheduled payments aobject
    // addend it onto the loan object for easy use
    loans.LoanList.forEach((loan) => {
      const matchingScheduledPayment = scheduledPayments.find(
        (payment) => payment.LoanNumber === loan.LoanNumber,
      );

      if (matchingScheduledPayment) {
        loan.ScheduledPayment = matchingScheduledPayment;
      }
    });

    yield put(setAuthLoans(loans));
    yield put(callAuthGetInternalAccounts());

    // Loading modal will be resolved in authFetchInternalAccounts saga
  } catch (error) {
    // Request didn't work

    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthGetLoanList() {
  yield takeEvery("AUTH_GET_LOANS", authGetLoanList);
}

export function* authFetchInternalAccounts() {
  try {
    // Loading modal will be open from authGetLoanList saga
    yield put(showLoadingModal());

    const internalAccounts = yield call(authGetInternalAccounts);
    yield put(storeAuthInternalAccounts(internalAccounts));

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthFetchInternalAccounts() {
  yield takeEvery("AUTH_GET_INTERNAL_ACCOUNTS", authFetchInternalAccounts);
}

export function* authSaveInternalInfo({ paymentDetails }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const response = yield call(authSaveInternal, paymentDetails);

    if (!response.InternalPaymentError && !response.PaymentError) {
      yield put(callStoreInternalResponse(response));
    } else {
      // For now, throw an error if there was a payment error
      yield put(showErrorNotification(response.PaymentError || response.InternalPaymentError));
    }

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthSaveInternalInfo() {
  yield takeEvery("AUTH_SAVE_INTERNAL", authSaveInternalInfo);
}

export function* authSubmitTransfer({ isCreditCard }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const response = isCreditCard
      ? yield call(authSubmitInternalTransferToCreditCard)
      : yield call(authSubmitInternalTransfer);

    if (!response.InternalPaymentError && !response.PaymentError) {
      yield put(callStorePaymentResponse(response));
    } else {
      // For now, throw an error if there was a payment error
      yield put(showErrorNotification(response.PaymentError || response.InternalPaymentError));
    }

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work

    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthSubmitTransfer() {
  yield takeEvery("AUTH_SUBMIT_INTERNAL", authSubmitTransfer);
}

export function* authCreatePaymentRecord({ loanNumber }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    yield call(createAuthPaymentRecord, loanNumber);

    const details = yield call(authGetAdditionalLoanDetails);
    yield put(setAuthAdditionalDetails(details));

    yield put(setAuthRecordCreated());

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work

    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthCreatePaymentRecord() {
  yield takeEvery("AUTH_CREATE_RECORD", authCreatePaymentRecord);
}

export function* authSaveDebitInfo({ paymentDetails }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const response = yield call(authSaveDebit, paymentDetails);

    if (!response.InternalPaymentError && !response.PaymentError) {
      yield put(callStoreDebitResponse(response));
    } else {
      // For now, throw an error if there was a payment error
      yield put(showErrorNotification(response.PaymentError || response.InternalPaymentError));
    }

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work

    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthSaveDebitInfo() {
  yield takeEvery("AUTH_SAVE_DEBIT", authSaveDebitInfo);
}

export function* authSubmitDebit() {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const response = yield call(authSubmitDebitPayment);

    if (!response.InternalPaymentError && !response.PaymentError) {
      yield put(callStorePaymentResponse(response));
    } else {
      // For now, throw an error if there was a payment error
      yield put(showErrorNotification(response.PaymentError || response.InternalPaymentError));
    }

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work

    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthSubmitDebit() {
  yield takeEvery("AUTH_SUBMIT_DEBIT", authSubmitDebit);
}

export function* authCalcAchPostDate({ paymentDetails }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const isScheduledPayment =
      paymentDetails.ScheduledPaymentDate &&
      moment(paymentDetails.ScheduledPaymentDate).isAfter(moment());

    if (isScheduledPayment) {
      paymentDetails.ScheduledPaymentSetup = {
        PaymentDate: paymentDetails.ScheduledPaymentDate,
      };
    }

    paymentDetails.ScheduledPaymentDate = undefined;

    const effectiveCalculateCall = isScheduledPayment
      ? calculateAchPostDateV2
      : calculateAchPostDate;

    const postDate = yield call(effectiveCalculateCall, paymentDetails);

    // Calc V1 returns straight post date, while Calc V2 returns an object with more details since it's used for validating recurring payments,
    // so we need to check which one we're using and pull the post date accordingly
    const postDateToStore = isScheduledPayment ? postDate.PostDate : postDate;

    yield put(storeAuthCalcPostDate(postDateToStore));

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work

    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthCalcAchPostDate() {
  yield takeEvery("AUTH_CALC_POST_DATE", authCalcAchPostDate);
}

export function* authCalcAchPostDateV2({ paymentDetails, cancel }) {
  // Since we use takeLatest, this will allow us to cancel this saga
  if (cancel) {
    return;
  }

  try {
    yield put(hideNotification());

    const recurringValidaton = yield call(calculateAchPostDateV2, paymentDetails);

    yield put(storeAuthCalcPostDateV2(recurringValidaton));
  } catch (error) {
    // Request didn't work

    yield put(setAuthError());
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthCalcAchPostDateV2() {
  yield takeLatest("AUTH_VALIDATE_RECURRING", authCalcAchPostDateV2);
}

export function* authSubmitAch({ paymentDetails }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const response = yield call(authSubmitAchPayment, paymentDetails);

    if (!response.InternalPaymentError && !response.PaymentError) {
      yield put(callStorePaymentResponse(response));
    } else {
      // For now, throw an error if there was a payment error
      yield put(showErrorNotification(response.PaymentError || response.InternalPaymentError));
    }

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthSubmitAch() {
  yield takeEvery("AUTH_SUBMIT_ACH", authSubmitAch);
}

export function* authCancelFutureDatedPayment({ paymentId, setLoading, setError, onSuccess }) {
  try {
    yield put(hideNotification());

    setLoading(true);

    const response = yield call(cancelFutureDatedPayment, paymentId);

    if (!response.ErrorMessage) {
      onSuccess();
    } else {
      setError(response.ErrorMessage);
    }

    setLoading(false);
  } catch (error) {
    // Request didn't work
    setError("An error occurred while cancelling your payment. Please try again.");

    setLoading(false);
  }
}

export function* watchAuthCancelFutureDatedPayment() {
  yield takeEvery("AUTH_CANCEL_FUTURE_DATED_PAYMENT", authCancelFutureDatedPayment);
}

export function* authRecurringCancellation({ cancelDetails }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const response = yield call(authCancelRecurring, cancelDetails);

    if (!response.InternalPaymentError) {
      yield put(callStorePaymentResponse(response));
    } else {
      // For now, throw an error if there was a payment error
      yield put(showErrorNotification(response.InternalPaymentError));
    }

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthRecurringCancellation() {
  yield takeEvery("AUTH_CANCEL_RECURRING", authRecurringCancellation);
}

export function* authPayoffCheck({ paymentDetails }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    let checkPayoffObject = {
      Amount: parseFloat(paymentDetails.Amount || paymentDetails.amount),
      AchOrDebit: paymentDetails.AchOrDebit,
      AchRoutingNumber: paymentDetails.RoutingNumber || "",
      AchAccountNumber: paymentDetails.AccountNumber || "",
      AchIsChecking: paymentDetails.isChecking,
      IsPrincipalOnly: paymentDetails.IsPrincipalOnly,
    };

    const response = yield call(checkPayoff, checkPayoffObject);

    if (response.PaymentIsAllowed) {
      yield put(callSaveAuthPayoffCheck(response));
    } else {
      yield put(showErrorNotification(response.Message));
    }

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* watchAuthPayoffCheck() {
  yield takeEvery("AUTH_CHECK_PAYOFF", authPayoffCheck);
}

export function* authGeneratePayoff({ accountHash }) {
  try {
    yield put(hideNotification());

    // This is a function that is tailored specfically to work in the DI mobile app
    // It must be a GET request called by clicking an anchor element
    const downloadAnchor = document.createElement("a");
    downloadAnchor.target = "_blank";

    let baseUrl = window.location.origin;
    if (!window.location.host.match(/^localhost/)) {
      baseUrl += "/api";
    }
    const token = sessionStorage.getItem("_LTjwt");

    downloadAnchor.href = `${baseUrl}/AuthenticatedLoanPayments/GeneratePayoff/${accountHash}?accesstoken=${token}`;
    downloadAnchor.click();
  } catch (error) {
    yield put(setAuthError());
  }
}

// Watch for dispatch of begin session action to call this saga
export function* watchAuthGeneratePayoff() {
  yield takeLatest("AUTH_GENERATE_PAYOFF_LETTER", authGeneratePayoff);
}

export function* authRefinanceRedirectSaga({ accountHash }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const response = yield call(getAuthRefinanceRedirect, accountHash);

    // use the redirect url to open the redirect link
    window.location.href = response.RedirectUrl;
  } catch (error) {
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* watchAuthRefinanceRedirect() {
  yield takeLatest("AUTH_REFINANCE_REDIRECT", authRefinanceRedirectSaga);
}



// WEBPACK FOOTER //
// ./src/sagas/authPayment.sagas.js